import { AuthContext } from '@/hooks/useAuth';
import { supabase } from '@/services/supabase';
import { AuthContextType, ForgotPasswordFormData, LoginFormData, Profile, ProfileUpdate, SignupFormData } from '@/types/auth';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';


export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: user, error } = useSuspenseQuery({
    queryKey: ['auth'],
    queryFn: async () => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) throw sessionError;
      if (!session) return null;

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        await supabase.auth.signOut();
        throw userError;
      }
      return user;
    }
  });

  const { data: profile } = useSuspenseQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    const channel = supabase
      .channel("schema-db-changes")
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'profiles' },
        payload => {
          queryClient.setQueryData(['profile'], (oldData: Profile | null) => {
            if (!oldData) return null;
            return { ...oldData, ...payload.new };
          });
        }
      );

    channel.subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [queryClient]);



  const signInMutation = useMutation({
    mutationFn: async ({ email, password }: LoginFormData) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (error) => {
      if (error instanceof Error) {
        throw new Error(error.message || 'An error occurred while signing in. Please try again.');
      }
      throw error;
    }
  });

  const signUpMutation = useMutation({
    mutationFn: async ({ email, password, firstName, lastName, phone }: SignupFormData) => {
      firstName = firstName.trim();
      lastName = lastName.trim();
      const fullName = `${firstName} ${lastName}`;
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            name: fullName,
            display_name: fullName,
            phone: phone
          },
          emailRedirectTo: window.location.origin
        }
      });
      if (error) throw error;
    },
    onError: (error) => {
      if (error instanceof Error) {
        throw new Error(error.message || 'An error occurred while signing up. Please try again.');
      }
      throw error;
    }
  });

  const signInWithGoogleMutation = useMutation({
    mutationFn: async () => {
      console.log("window.location.origin", window.location.origin);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (error) => {
      throw new Error(error.message || 'Failed to sign in with Google. Please try again.');
    }
  });

  const signOutMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      await navigate({ to: '/' });
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: async ({ email }: ForgotPasswordFormData) => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password',
      });

      if (error) throw error;
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (updates: ProfileUpdate) => {
      if (!user?.id) throw new Error('No user logged in');

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        queryClient.setQueryData(['auth'], null);
        queryClient.setQueryData(['profile'], null);
      } else if (session?.user) {
        queryClient.setQueryData(['auth'], session.user);
        queryClient.refetchQueries({ queryKey: ['profile'] });
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const value: AuthContextType = {
    profile,
    error: error?.message ?? null,
    signIn: signInMutation.mutateAsync,
    signUp: signUpMutation.mutateAsync,
    forgotPassword: forgotPasswordMutation.mutateAsync,
    signInWithGoogle: signInWithGoogleMutation.mutateAsync,
    signOut: signOutMutation.mutateAsync,
    updateProfileMutation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}